import React from 'react'
import { Link } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import { LanguagePicker } from '../ui/LanguagePicker'
import { CONTACT, MAILTO } from '../../config/contact'
import BrandMark from './BrandMark'

interface FooterLink {
  labelId: string
  to?: string
  href?: string
  external?: boolean
}

const LINK_GROUPS: Array<{ categoryId: string; links: FooterLink[] }> = [
  {
    categoryId: 'marketing.footer.product',
    links: [
      { labelId: 'marketing.footer.parcours', href: '#parcours' },
      { labelId: 'marketing.footer.templates', to: '/surveys/templates' },
    ],
  },
  {
    categoryId: 'marketing.footer.resources',
    links: [
      { labelId: 'marketing.footer.developers', to: '/developers' },
      { labelId: 'marketing.footer.apiDocs', to: '/api-docs' },
      { labelId: 'marketing.footer.styleGuide', to: '/style-guide' },
    ],
  },
  {
    categoryId: 'marketing.footer.company',
    links: [
      { labelId: 'marketing.footer.bookCall', href: CONTACT.bookingUrl, external: true },
      { labelId: 'marketing.footer.contact', href: MAILTO, external: true },
      { labelId: 'marketing.footer.aboutCatalyzeUp', href: 'https://catalyzeup.ai', external: true },
    ],
  },
]

const FooterSection: React.FC = () => {
  return (
    <footer className="bg-surface border-t border-border-light py-8 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8">
          <div>
            <h3 className="flex items-center gap-2 text-lg font-heading font-bold text-primary-dark mb-2">
              <BrandMark className="h-6 w-6" />
              Sondely
            </h3>
            <p className="text-sm text-foreground-muted"><FormattedMessage id="marketing.footer.tagline" /></p>
            <p className="text-sm text-foreground-muted mt-3">
              <a href={MAILTO} className="hover:text-foreground transition-colors">{CONTACT.email}</a>
            </p>
          </div>

          {LINK_GROUPS.map(({ categoryId, links }) => (
            <div key={categoryId}>
              <h4 className="text-sm font-semibold text-foreground mb-3"><FormattedMessage id={categoryId} /></h4>
              <ul className="space-y-2">
                {links.map((link, i) => (
                  <li key={i}>
                    {link.to ? (
                      <Link to={link.to} className="text-sm text-foreground-muted hover:text-foreground transition-colors">
                        <FormattedMessage id={link.labelId} />
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        className="text-sm text-foreground-muted hover:text-foreground transition-colors"
                        {...(link.external ? { target: '_blank', rel: 'noreferrer' } : {})}
                        onClick={link.href?.startsWith('#') ? (e) => {
                          e.preventDefault()
                          document.querySelector(link.href!)?.scrollIntoView({ behavior: 'smooth' })
                        } : undefined}
                      >
                        <FormattedMessage id={link.labelId} />
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-8 pt-8 border-t border-border-light flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-foreground-muted">
          <span><FormattedMessage id="marketing.footer.copyright" values={{ year: new Date().getFullYear() }} /></span>
          <LanguagePicker />
        </div>
      </div>
    </footer>
  )
}

export default FooterSection
